import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { toast } from "sonner";
import { Ticket, Plus, Ban, CalendarDays, X } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { format } from "date-fns";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";

const EMPTY = { code: "", kind: "percent", value: "", expires_on: "", max_uses: "" };
const field = "h-9 bg-[#0F172A] border border-slate-700 rounded px-2 text-sm num text-white focus:outline-none focus:border-emerald-500";
const fmtOff = (p) => (p.kind === "percent" ? `${p.value}% off` : `A$${Number(p.value).toFixed(2)} off`);
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString("en-AU", { day: "2-digit", month: "short", year: "numeric" }) : "No expiry");

const Labeled = ({ label, children }) => (
  <label className="flex flex-col gap-1.5"><span className="text-xs text-slate-400">{label}</span>{children}</label>
);

const ExpiryPicker = ({ value, onChange }) => {
  const [open, setOpen] = useState(false);
  const today = new Date(); today.setHours(0, 0, 0, 0);
  return (
    <div className="flex items-center gap-1">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button type="button" className={`${field} flex-1 flex items-center gap-2 text-left`} data-testid="promo-expiry-input">
            <CalendarDays className="h-4 w-4 text-slate-500" />
            <span className={value ? "text-white" : "text-slate-500"}>{value ? format(new Date(`${value}T00:00:00`), "dd MMM yyyy") : "No expiry"}</span>
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start" data-testid="promo-expiry-calendar">
          <Calendar mode="single" selected={value ? new Date(`${value}T00:00:00`) : undefined} disabled={{ before: today }}
            onSelect={(d) => { onChange(d ? format(d, "yyyy-MM-dd") : ""); setOpen(false); }} initialFocus />
        </PopoverContent>
      </Popover>
      {value && <button type="button" onClick={() => onChange("")} className="p-1 text-slate-500 hover:text-white" data-testid="promo-expiry-clear"><X className="h-4 w-4" /></button>}
    </div>
  );
};

const ConfirmOff = ({ promo, onCancel, onConfirm }) => (
  <AlertDialog open={!!promo} onOpenChange={(o) => !o && onCancel()}>
    <AlertDialogContent data-testid="promo-deactivate-dialog">
      <AlertDialogHeader>
        <AlertDialogTitle>Turn off {promo?.code}?</AlertDialogTitle>
        <AlertDialogDescription>Customers will no longer be able to use this code at checkout. This can't be undone, but you can create a new code with the same name.</AlertDialogDescription>
      </AlertDialogHeader>
      <AlertDialogFooter>
        <AlertDialogCancel data-testid="promo-deactivate-cancel">Keep it</AlertDialogCancel>
        <AlertDialogAction onClick={onConfirm} className="bg-rose-600 hover:bg-rose-500" data-testid="promo-deactivate-confirm">Turn off</AlertDialogAction>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>
);

const PromoForm = ({ onCreated }) => {
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/admin/promos", {
        code: f.code.trim(), kind: f.kind, value: Number(f.value),
        expires_on: f.expires_on || null, max_uses: f.max_uses ? Number(f.max_uses) : null,
      });
      toast.success(`Promo code ${data.code} created`);
      setF(EMPTY);
      onCreated();
    } catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="panel p-5 grid grid-cols-2 md:grid-cols-6 gap-4 items-end" data-testid="promo-form">
      <Labeled label="Code"><input required value={f.code} onChange={set("code")} placeholder="WELCOME20" className={`${field} uppercase`} data-testid="promo-code-input" /></Labeled>
      <Labeled label="Discount type">
        <select value={f.kind} onChange={set("kind")} className={field} data-testid="promo-kind-select">
          <option value="percent">% off</option><option value="amount">A$ off</option>
        </select>
      </Labeled>
      <Labeled label={f.kind === "percent" ? "Percent off" : "Amount off (A$)"}><input required type="number" min="0.01" step="0.01" value={f.value} onChange={set("value")} className={field} data-testid="promo-value-input" /></Labeled>
      <Labeled label="Expires (optional)"><ExpiryPicker value={f.expires_on} onChange={(v) => setF({ ...f, expires_on: v })} /></Labeled>
      <Labeled label="Max uses (optional)"><input type="number" min="1" step="1" value={f.max_uses} onChange={set("max_uses")} className={field} data-testid="promo-max-uses-input" /></Labeled>
      <Button type="submit" disabled={busy} data-testid="promo-create-button"><Plus className="h-4 w-4 mr-1" />{busy ? "Creating..." : "Create code"}</Button>
    </form>
  );
};

const PromoRow = ({ p, onDeactivate }) => (
  <tr className={`border-t border-slate-800 ${p.active ? "" : "opacity-60"}`} data-testid={p.active ? `promo-row-${p.code}` : `promo-row-off-${p.id}`}>
    <td className="py-3 px-4 font-mono text-white">{p.code}</td>
    <td className="py-3 px-4 text-emerald-300 num">{fmtOff(p)}</td>
    <td className="py-3 px-4 num text-slate-300">{p.times_used}{p.max_uses ? ` / ${p.max_uses}` : ""}</td>
    <td className="py-3 px-4 num text-slate-400">{fmtDate(p.expires_at)}</td>
    <td className="py-3 px-4">
      <span className={`text-[11px] font-mono px-2 py-0.5 rounded border ${p.active ? "text-emerald-300 border-emerald-500/40" : "text-slate-500 border-slate-700"}`} data-testid={p.active ? `promo-status-${p.code}` : `promo-status-off-${p.id}`}>{p.active ? "Active" : "Off"}</span>
    </td>
    <td className="py-3 px-4 text-right">
      {p.active && <button onClick={() => onDeactivate(p)} className="text-xs text-rose-300 hover:text-rose-200 inline-flex items-center gap-1" data-testid={`promo-deactivate-${p.code}`}><Ban className="h-3.5 w-3.5" />Turn off</button>}
    </td>
  </tr>
);

export default function Promos() {
  const { user } = useAuth();
  const [rows, setRows] = useState(null);
  const [pending, setPending] = useState(null);
  const load = () => api.get("/admin/promos").then((r) => setRows([...r.data].sort((a, b) => b.active - a.active))).catch((e) => toast.error(formatApiError(e)));
  useEffect(() => { if (user?.role === "admin") load(); }, [user]);
  if (user && user.role !== "admin") return <Navigate to="/program" replace />;

  const deactivate = async () => {
    const p = pending;
    setPending(null);
    try { await api.post(`/admin/promos/${p.id}/deactivate`); toast.success(`${p.code} turned off`); load(); } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-6">
      <div className="fade-up">
        <div className="eyebrow mb-2">Admin • Stripe promotion codes</div>
        <h1 className="text-4xl sm:text-5xl font-bold flex items-center gap-3" data-testid="promos-title"><Ticket className="h-9 w-9 text-emerald-400" />Promo Codes</h1>
        <p className="text-slate-400 mt-1 text-sm">Customers type the code on the Stripe checkout page. Each code applies once per purchase.</p>
      </div>
      <PromoForm onCreated={load} />
      <ConfirmOff promo={pending} onCancel={() => setPending(null)} onConfirm={deactivate} />
      <div className="panel overflow-x-auto">
        <table className="w-full text-sm" data-testid="promos-table">
          <thead><tr className="text-left text-xs uppercase tracking-wider text-slate-500">
            <th className="py-3 px-4">Code</th><th className="py-3 px-4">Discount</th><th className="py-3 px-4">Used</th><th className="py-3 px-4">Expires</th><th className="py-3 px-4">Status</th><th />
          </tr></thead>
          <tbody>
            {rows === null && <tr><td colSpan={6} className="py-8 text-center text-slate-500">Loading...</td></tr>}
            {rows?.length === 0 && <tr><td colSpan={6} className="py-8 text-center text-slate-500" data-testid="promos-empty">No promo codes yet</td></tr>}
            {rows?.map((p) => <PromoRow key={p.id} p={p} onDeactivate={setPending} />)}
          </tbody>
        </table>
      </div>
    </div>
  );
}
